import { FormField } from '@crocodille/common';
import { isSome, Maybe, VALID_PHONE_FORMATS } from '@crocodille/util';
import React, { useRef, useState } from 'react';
import Select from '../select-2/select-2';
import InputEnvelope from '../input-envelope/input-envelope';
import Input from '../input-2/input-2';

import styles from './phone-input.module.scss';

const COUNTRY_TO_LABEL: Record<string, string> = {
  cz: '(CZ)',
  sk: '(SK)',
  by: '(BY)',
  fr: '(FR)',
  it: '(IT)',
  de: '(DE)',
  pl: '(PL)',
  pt: '(PT)',
  at: '(AT)',
  ru: '(RU)',
  es: '(ES)',
  ua: '(UA)',
};

const TEL_FORMATS = VALID_PHONE_FORMATS.map((format) => {
  return {
    countryCodeMatch: new RegExp(
      `^\\s*\\+\\s*${format.prefix.split('').join('\\s*')}\\s*`,
    ),
    countryCode: `+${format.prefix}`,
    country: format.countyCode,
    nationalTelNumberCount: format.numberCount,
  };
});

const MAX_TEL_NUMBER_COUNT = Math.max(
  ...TEL_FORMATS.map(
    (format) => format.nationalTelNumberCount + format.countryCode.length,
  ),
);

function splitPhoneNumber(
  value: string | undefined,
): readonly [countryCode: string | undefined, telNational: string | undefined] {
  if (value === undefined) {
    return [undefined, undefined];
  }

  const foundFormat = TEL_FORMATS.find((format) =>
    format.countryCodeMatch.test(value),
  );

  if (!foundFormat) {
    return [undefined, value];
  }

  return [
    foundFormat.countryCode,
    value
      .replace(foundFormat.countryCodeMatch, '')
      .replace(/\s/g, '')
      .replace(/[^0-9,+]/g, ''),
  ];
}

function joinPhoneNumber(
  countryCode: string | undefined,
  telNational: string | undefined,
) {
  return `${countryCode ?? ''}${telNational ?? ''}`;
}

export interface PhoneInputProps {
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly label?: string;
  readonly error?: Maybe<string>;
  readonly name?: string;
  readonly onBlur?: () => void;
  readonly defaultValue?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  required = false,
  disabled = false,
  invalid = false,
  label,
  error,
  name,
  defaultValue = '+420',
}) => {
  const [innerValue, setInnerValue] = useState(value ?? defaultValue ?? '');

  const splitNumber = splitPhoneNumber(value ?? innerValue);
  const splitNumberRef = useRef(splitNumber);
  splitNumberRef.current = splitNumber;

  const [countryCode, telNational] = splitNumber;

  const handleCountryCodeChange = (countryCodeValue: string) => {
    const [_, telNational] = splitNumberRef.current;
    const joined = joinPhoneNumber(countryCodeValue, telNational);

    onChange?.(joined);
    setInnerValue(joined);
  };

  const handleTelNationalChange = (telNationalValue: string) => {
    const [countryCode, _] = splitNumberRef.current;

    // Filter out non-digits immediately
    const digitsOnly = telNationalValue.replace(/[^0-9]/g, '');

    if (
      TEL_FORMATS.some((format) => format.countryCodeMatch.test(digitsOnly))
    ) {
      onChange?.(digitsOnly);
      setInnerValue(digitsOnly);
    } else {
      const joined = joinPhoneNumber(countryCode, digitsOnly);
      onChange?.(joined);
      setInnerValue(joined);
    }
  };

  return (
    <InputEnvelope
      innerClassName={styles.container}
      label={label}
      error={error}
      required={required}
      noLabelElement
    >
      {isSome(name) ? (
        <input
          type="hidden"
          name={name}
          value={value ?? innerValue}
          required={required}
          disabled={disabled}
        />
      ) : null}
      <Select
        className={styles.countryCodeInput}
        value={countryCode ?? ''}
        onChange={handleCountryCodeChange}
        onBlur={onBlur}
        disabled={disabled}
        invalid={invalid}
      >
        {!required ? <Select.Item value={''} label="" /> : null}
        {TEL_FORMATS.map((format) => (
          <Select.Item
            key={format.countryCode}
            value={format.countryCode}
            label={`${COUNTRY_TO_LABEL[format.country]} ${format.countryCode}`}
          />
        ))}
      </Select>
      <Input
        className={styles.telNationalInput}
        type="tel"
        autoComplete="tel"
        required={required}
        disabled={disabled}
        invalid={invalid}
        value={telNational}
        onChange={handleTelNationalChange}
        onBlur={onBlur}
        pattern={`[0-9]+`}
        minLength={
          TEL_FORMATS.find((format) => format.countryCode === countryCode)
            ?.nationalTelNumberCount ?? MAX_TEL_NUMBER_COUNT
        }
        maxLength={
          TEL_FORMATS.find((format) => format.countryCode === countryCode)
            ?.nationalTelNumberCount ?? MAX_TEL_NUMBER_COUNT
        }
      />
    </InputEnvelope>
  );
};

type FieldProps = 'value' | 'onChange' | 'disabled' | 'onBlur' | 'required';

export function fieldToPhoneInputProps(
  field: Pick<FormField<Maybe<string>>, 'error' | FieldProps>,
) {
  return {
    invalid: isSome(field.error),
    value: field.value === null ? '' : field.value,
    onChange: field.onChange,
    onBlur: field.onBlur,
    disabled: field.disabled,
    required: field.required,
  } satisfies Pick<PhoneInputProps, 'invalid' | FieldProps>;
}

export function filedToPhoneInputErrorTranslationParams(
  field: Pick<FormField<Maybe<string>>, 'error' | FieldProps>,
) {
  const splitNumber = splitPhoneNumber(field.value ?? '');
  const [countryCode, _] = splitNumber;

  return {
    length:
      TEL_FORMATS.find((format) => format.countryCode === countryCode)
        ?.nationalTelNumberCount ?? MAX_TEL_NUMBER_COUNT,
  };
}

export default PhoneInput;
